import { useState } from "react";
import { useFetch } from "../lib/useFetch";
import { fmt, ts, pnlClass } from "../lib/api";
import { Panel, Tag, PageHead, Spinner, Stat, Empty } from "../components/common";
import { SetupPerformance } from "../components/SetupPerformance";
import { AiReview } from "../components/AiReview";
import { IndexFilter } from "../components/IndexFilter";
import { Research } from "../components/Research";

const MARKETS = ["ALL", "BURSA", "US", "HK", "SG"];
const ACTIONS = ["ALL", "BUY", "SELL"];
const pct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${fmt(v, 2)}%`);

function Filter({ items, value, set, prefix }) {
  return (
    <div className="flex gap-1">
      {items.map((x) => <button key={x} data-testid={`${prefix}-${x.toLowerCase()}`} onClick={() => set(x)} className={`mono rounded-sm border px-3 py-1 text-[11px] ${value === x ? "border-blue-500 bg-blue-600 text-white" : "border-[#E2E8F0] text-slate-600 hover:bg-[#F1F5F9]"}`}>{x}</button>)}
    </div>
  );
}

export default function SignalHistory() {
  const [m, setM] = useState("ALL");
  const [a, setA] = useState("ALL");
  const { data, loading } = useFetch(`/signals/history${m === "ALL" ? "" : `?market=${m}`}`, 600000, [m]);
  const rows = (data?.rows || []).filter((r) => a === "ALL" || r.action === a);
  return (
    <div data-testid="signal-history-page">
      <PageHead title="Signal History" sub={data?.note || "Past BUY / SELL signals and what the price did afterwards."} />
      <div className="mb-3 flex flex-wrap gap-3"><Filter items={MARKETS} value={m} set={setM} prefix="sighist-market" /><Filter items={ACTIONS} value={a} set={setA} prefix="sighist-action" /></div>
      <Research />
      <SetupPerformance />
      <AiReview />
      <IndexFilter />
      {data && (
        <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat testid="sighist-buy-count" label="BUY signals" value={data.stats.BUY.count} sub={`${data.stats.BUY.right_pct}% price went up since`} />
          <Stat testid="sighist-sell-count" label="SELL signals" value={data.stats.SELL.count} sub={`${data.stats.SELL.right_pct}% price went down since`} />
          <Stat label="Timeframe" value={data.timeframe} sub={`generated ${ts(data.generated_at)}`} />
          <Stat label="Long-only" value="No shorts" sub="SELL = exit shares you hold" />
        </div>
      )}
      <Panel testid="sighist-table">
        {loading && !data ? <Spinner /> : !rows.length ? <Empty>No signals in the recent history</Empty> : (
          <div className="custom-scrollbar overflow-x-auto">
            <table className="tbl w-full">
              <thead><tr><th>Time</th><th>Symbol</th><th>Exch</th><th>Signal</th><th>Price</th><th>After 5 bars</th><th>After 20 bars</th><th>Latest</th><th>Since signal</th><th>Result</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={`${r.symbol}-${r.time}`} data-testid={`sighist-row-${r.symbol}-${r.time}`}>
                  <td className="mono text-slate-600">{ts(new Date(r.time * 1000).toISOString())}</td>
                  <td className="mono text-slate-900">{r.symbol}<div className="text-[10px] text-slate-500">{r.name}</div></td>
                  <td className="text-slate-600">{r.exchange}</td><td><Tag v={r.action} /></td>
                  <td className="mono">{r.price}</td><td className={`mono ${pnlClass(r.after_5_bars_pct)}`}>{pct(r.after_5_bars_pct)}</td>
                  <td className={`mono ${pnlClass(r.after_20_bars_pct)}`}>{pct(r.after_20_bars_pct)}</td><td className="mono">{r.latest_price}</td>
                  <td className={`mono ${pnlClass(r.since_signal_pct)}`}>{pct(r.since_signal_pct)}</td>
                  <td><span className={`mono text-[10px] font-bold ${r.verdict === "RIGHT" ? "text-emerald-600" : r.verdict === "WRONG" ? "text-red-600" : "text-slate-500"}`}>{r.verdict}</span></td>
                </tr>))}</tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
